import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Users, Trophy, BarChart3, TrendingUp, Loader2, ArrowLeft, UserPlus, ChevronDown, Check, X, Ruler, Scale, Flame, Target, Star, Activity } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, RadarChart, PolarGrid, PolarAngleAxis, Radar } from 'recharts';
import SafeResponsiveContainer from './ui/SafeResponsiveContainer';
import { toast } from 'sonner';

import { API } from '../config/api';

export default function TeamAnalytics({ team, onBack, onUpdate }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [availableAthletes, setAvailableAthletes] = useState([]);
  const [selectedAthletes, setSelectedAthletes] = useState([]);
  const [importing, setImporting] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/teams/${team.id}/stats`);
      setStats(res.data);
    } catch (err) {
      toast.error('Failed to load team stats');
    } finally {
      setLoading(false);
    }
  }, [team?.id]);

  const fetchAvailableAthletes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/children`);
      // Filter out athletes already in this team
      const available = res.data.filter(c => !team.member_ids?.includes(c.id));
      setAvailableAthletes(available);
    } catch (err) {
      console.error('Failed to fetch athletes');
    }
  }, [team?.member_ids]);

  useEffect(() => {
    if (team?.id) {
      fetchStats();
      fetchAvailableAthletes();
    }
  }, [team?.id, fetchStats, fetchAvailableAthletes]);

  const bulkImportAll = async () => {
    setImporting(true);
    try {
      const res = await axios.post(`${API}/teams/${team.id}/bulk-import`);
      toast.success(res.data.message);
      fetchStats();
      fetchAvailableAthletes();
      onUpdate?.();
    } catch (err) {
      toast.error('Failed to import athletes');
    } finally {
      setImporting(false);
    }
  };

  const addSelectedAthletes = async () => {
    if (selectedAthletes.length === 0) return;
    setImporting(true);
    try {
      const res = await axios.post(`${API}/teams/${team.id}/bulk-add`, { child_ids: selectedAthletes });
      toast.success(res.data.message);
      setSelectedAthletes([]);
      setShowAddMembers(false);
      fetchStats();
      fetchAvailableAthletes();
      onUpdate?.();
    } catch (err) {
      toast.error('Failed to add athletes');
    } finally {
      setImporting(false);
    }
  };

  const toggleAthleteSelection = (id) => {
    setSelectedAthletes(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#02b5ff]" />
      </div>
    );
  }

  const performanceData = stats?.performance ? Object.entries(stats.performance).map(([name, data]) => ({
    name: name.length > 15 ? name.substring(0, 15) + '...' : name,
    fullName: name,
    avg: data.avg?.toFixed(2) || 0,
    best: data.best?.toFixed(2) || 0,
    count: data.count || 0
  })).slice(0, 6) : [];

  return (
    <div className="space-y-6" data-testid="team-analytics">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button onClick={onBack} variant="ghost" className="text-zinc-400 hover:text-white">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${team.color}20`, borderColor: team.color, borderWidth: 2 }}
            >
              <Users className="h-6 w-6" style={{ color: team.color }} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">{team.name}</h2>
              <p className="text-sm text-zinc-500">{team.sport || 'Multi-sport'} • {stats?.stats?.total_members || 0} athletes</p>
            </div>
          </div>
        </div>
        <Button
          onClick={() => setShowAddMembers(!showAddMembers)}
          className="bg-gradient-to-r from-[#02b5ff] to-[#0099dd] text-white rounded-full"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Add Athletes
        </Button>
      </div>

      {/* Add Members Panel */}
      {showAddMembers && (
        <div className="bg-zinc-900/80 border border-zinc-700 rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-white">Add Athletes to Team</h3>
            <div className="flex gap-2">
              <Button 
                onClick={bulkImportAll} 
                disabled={importing || availableAthletes.length === 0}
                variant="outline" 
                className="text-sm border-zinc-700 text-zinc-300"
              >
                Import All Unassigned
              </Button>
              <Button 
                onClick={addSelectedAthletes} 
                disabled={importing || selectedAthletes.length === 0}
                className="text-sm bg-[#02b5ff]"
              >
                {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : `Add Selected (${selectedAthletes.length})`}
              </Button>
            </div>
          </div>
          
          {availableAthletes.length === 0 ? (
            <p className="text-zinc-500 text-sm">All athletes are already assigned to teams</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-48 overflow-y-auto">
              {availableAthletes.map((athlete) => (
                <button
                  key={athlete.id}
                  onClick={() => toggleAthleteSelection(athlete.id)}
                  className={`p-2 rounded-lg text-left text-sm transition-all ${
                    selectedAthletes.includes(athlete.id)
                      ? 'bg-[#02b5ff]/20 border-[#02b5ff] border'
                      : 'bg-zinc-800/50 border-zinc-700 border hover:border-zinc-600'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-5 h-5 rounded flex items-center justify-center ${
                      selectedAthletes.includes(athlete.id) ? 'bg-[#02b5ff]' : 'bg-zinc-700'
                    }`}>
                      {selectedAthletes.includes(athlete.id) && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <span className="text-white truncate">{athlete.first_name} {athlete.last_name}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stats Cards - Row 1: Core Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-center">
          <Users className="h-6 w-6 text-[#02b5ff] mx-auto mb-2" />
          <div className="text-2xl font-bold text-white">{stats?.stats?.total_members || 0}</div>
          <div className="text-xs text-zinc-500">Athletes</div>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-center">
          <TrendingUp className="h-6 w-6 text-[#ff025f] mx-auto mb-2" />
          <div className="text-2xl font-bold text-white">{stats?.stats?.avg_age?.toFixed(1) || 0}</div>
          <div className="text-xs text-zinc-500">Avg Age</div>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-center">
          <BarChart3 className="h-6 w-6 text-green-500 mx-auto mb-2" />
          <div className="text-2xl font-bold text-white">{stats?.stats?.total_tests || 0}</div>
          <div className="text-xs text-zinc-500">Total Tests</div>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-center">
          <Ruler className="h-6 w-6 text-yellow-500 mx-auto mb-2" />
          <div className="text-2xl font-bold text-white">{stats?.stats?.total_measurements || 0}</div>
          <div className="text-xs text-zinc-500">Measurements</div>
        </div>
      </div>

      {/* Stats Cards - Row 2: Gamification & Engagement */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-xl p-4 text-center">
          <Star className="h-6 w-6 text-yellow-500 mx-auto mb-2" />
          <div className="text-2xl font-bold text-yellow-400">{stats?.stats?.team_points?.toLocaleString() || 0}</div>
          <div className="text-xs text-zinc-400">Team Points</div>
        </div>
        <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-xl p-4 text-center">
          <Flame className="h-6 w-6 text-orange-500 mx-auto mb-2" />
          <div className="text-2xl font-bold text-orange-400">{stats?.stats?.active_streaks || 0}</div>
          <div className="text-xs text-zinc-400">Active Streaks</div>
        </div>
        <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-4 text-center">
          <Target className="h-6 w-6 text-green-500 mx-auto mb-2" />
          <div className="text-2xl font-bold text-green-400">
            {stats?.stats?.completed_targets || 0}/{stats?.stats?.total_targets || 0}
          </div>
          <div className="text-xs text-zinc-400">Targets Complete</div>
        </div>
        <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-4 text-center">
          <Activity className="h-6 w-6 text-purple-500 mx-auto mb-2" />
          <div className="text-2xl font-bold text-purple-400">{stats?.stats?.sessions_this_week || 0}</div>
          <div className="text-xs text-zinc-400">Sessions (7 days)</div>
        </div>
      </div>

      {/* Target Completion Progress Bar */}
      {stats?.stats?.total_targets > 0 && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-white flex items-center gap-2">
              <Target className="h-4 w-4 text-green-500" />
              Team Target Progress
            </h4>
            <span className="text-sm font-bold text-green-400">{stats?.stats?.target_completion_rate || 0}%</span>
          </div>
          <div className="w-full h-3 bg-zinc-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-green-500 to-emerald-400 transition-all duration-500"
              style={{ width: `${stats?.stats?.target_completion_rate || 0}%` }}
            />
          </div>
          <p className="text-xs text-zinc-500 mt-2">
            {stats?.stats?.completed_targets || 0} of {stats?.stats?.total_targets || 0} targets achieved this season
          </p>
        </div>
      )}

      {/* Performance Chart */}
      {performanceData.length > 0 && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Trophy className="h-5 w-5 text-yellow-500" />
            Team Performance by Test
          </h3>
          <div className="h-64">
            <SafeResponsiveContainer width="100%" height="100%" minHeight={200}>
              <BarChart data={performanceData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="name" tick={{ fill: '#888', fontSize: 11 }} />
                <YAxis tick={{ fill: '#888', fontSize: 11 }} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: 8 }}
                  labelStyle={{ color: '#fff' }}
                />
                <Bar dataKey="avg" fill="#02b5ff" name="Team Average" radius={[4, 4, 0, 0]} />
                <Bar dataKey="best" fill="#ff025f" name="Best Result" radius={[4, 4, 0, 0]} />
              </BarChart>
            </SafeResponsiveContainer>
          </div>
        </div>
      )}

      {/* Members List */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-yellow-500" />
            Team Leaderboard
          </span>
          <span className="text-sm text-zinc-500 font-normal">Ranked by Points</span>
        </h3>
        {stats?.members?.length === 0 ? (
          <p className="text-zinc-500 text-center py-8">No members yet. Add athletes to get started!</p>
        ) : (
          <div className="grid gap-3">
            {stats?.members?.map((member, idx) => (
              <div key={member.id} className={`flex items-center justify-between rounded-lg p-3 ${
                member.team_rank === 1 ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/10 border border-yellow-500/30' :
                member.team_rank === 2 ? 'bg-gradient-to-r from-zinc-400/20 to-zinc-500/10 border border-zinc-400/30' :
                member.team_rank === 3 ? 'bg-gradient-to-r from-amber-700/20 to-amber-800/10 border border-amber-700/30' :
                'bg-zinc-800/50'
              }`}>
                <div className="flex items-center gap-3">
                  {/* Rank Badge */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    member.team_rank === 1 ? 'bg-yellow-500 text-black' :
                    member.team_rank === 2 ? 'bg-zinc-400 text-black' :
                    member.team_rank === 3 ? 'bg-amber-700 text-white' :
                    'bg-zinc-700 text-zinc-300'
                  }`}>
                    #{member.team_rank || idx + 1}
                  </div>
                  {member.photo ? (
                    <img src={`${API}${member.photo}`} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 bg-gradient-to-br from-[#02b5ff]/30 to-[#ff025f]/30 rounded-full flex items-center justify-center">
                      <Users className="h-5 w-5 text-[#02b5ff]" />
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-white">{member.name}</p>
                    <p className="text-xs text-zinc-500">
                      Age: {member.age?.toFixed(1)} • {member.sport || 'Multi-sport'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  {/* Points */}
                  <div className="text-center px-2">
                    <div className="text-yellow-400 font-bold flex items-center gap-1">
                      <Star className="h-3 w-3" />
                      {member.points?.toLocaleString() || 0}
                    </div>
                    <div className="text-xs text-zinc-500">Points</div>
                  </div>
                  {/* Streak */}
                  {member.streak > 0 && (
                    <div className="text-center px-2">
                      <div className="text-orange-400 font-bold flex items-center gap-1">
                        <Flame className="h-3 w-3" />
                        {member.streak}
                      </div>
                      <div className="text-xs text-zinc-500">Streak</div>
                    </div>
                  )}
                  {/* Sessions this week */}
                  <div className="text-center px-2">
                    <div className="text-purple-400 font-bold">{member.sessions_this_week || 0}</div>
                    <div className="text-xs text-zinc-500">Sessions</div>
                  </div>
                  {/* Tests */}
                  <div className="text-center px-2">
                    <div className="text-[#02b5ff] font-semibold">{member.test_count}</div>
                    <div className="text-xs text-zinc-500">Tests</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
